// Typed error for every failed API call. Panes catch this and show `.message`,
// and can branch on `.code` (e.g. VALIDATION_FAILED, PERMISSION_DENIED, CONFLICT).
export class AppError extends Error {
    code;
    status;
    details;
    constructor(code, message, status = 0, details) {
        super(message || code);
        this.name = "AppError";
        this.code = code;
        this.status = status;
        this.details = details;
    }
}
/** True when the failure is an authz/authn problem (surface a sign-in / permission hint). */
export function isAuthError(e) {
    return e instanceof AppError && (e.status === 401 || e.status === 403);
}
/**
 * True when the tenant's own configuration switched this process OFF (a Hub suppress rule —
 * multi-client-upgrade §5.3). Panes branch on this to render "not available for your
 * organisation" instead of a failure: the platform did exactly what it was told, and telling
 * the user something broke would send them to support about a setting their admin chose.
 */
export function isFeatureDisabled(e) {
    return e instanceof AppError && e.code === "FLOW_SUPPRESSED_FOR_TENANT";
}
/** The user-facing sentence for a suppressed feature — one spelling, everywhere. */
export const FEATURE_DISABLED_MESSAGE = "This feature is not enabled for your organisation.";
/**
 * True when a write was refused because the ORDER BEHIND IT IS ALREADY CLOSED — the screen that
 * sent it is looking at a sale somebody else finished (2026-09-09).
 *
 * orders refuses a status write out of `completed`/`cancelled`/`reversed` with `SESSION_LOCKED`
 * (see clienteling.ts's assertSaleNotClosed), and `confirm` has always refused a re-book of an
 * edit-locked session the same way. Both mean one thing to the person at the screen: this card is
 * stale, and no amount of tapping it again will help.
 *
 * Matched on BOTH shapes, because the same refusal reaches a pane by two roads: an `AppError` from
 * a direct PATCH (`code` is the CANONICAL `RESOURCE_LOCKED` — orders' catalogue aliases
 * `SESSION_LOCKED` onto it, and the sentence keeps the code as written), and a `FlowRun` whose
 * `failCode`/`message` carry the same pair out of the Hub's trace. Duck-typed rather than
 * overloaded so a caller can hand it either without unwrapping first.
 *
 * Callers must treat a true here as NEW INFORMATION, not as a failed attempt: drop the row and
 * re-read, never restore it. Putting the card back is what turns one stale tap into a queue nobody
 * can clear — see staffstockpick's sendToCounter.
 */
export function isOrderClosed(e) {
    const code = String(e?.code ?? e?.failCode ?? "");
    const message = String(e?.message ?? "");
    return code === "RESOURCE_LOCKED" || /SESSION_LOCKED/.test(message);
}
/** The user-facing sentence for that refusal — one spelling, everywhere. */
export const ORDER_CLOSED_MESSAGE = "This order has already been sold and paid — it can't be changed. Raise a return instead.";
/**
 * True when a claim was refused because SOMEBODY ELSE IS ALREADY PICKING THIS ORDER (2026-09-18).
 *
 * The shop reported two PCs taking the same pick task. Neither picking screen could stop it — the
 * Take-task button reflects a list read seconds old — so orders now refuses the second claim
 * outright (`assertPickerFree` over `ClientelingSessionAdapter.claimPicker`, whose WHERE is the
 * rule). No takeover: the shop ruled on that, 2026-09-18.
 *
 * Matched on the message as well as the code for the reason `BASKET_IN_USE` is: `PICK_TAKEN` is
 * not in the shared error catalogue (`helpers/errors.ts`, byte-identical across all twelve
 * services), so it reaches the browser as a generic `CONFLICT` with the sentence intact.
 *
 * Like `isOrderClosed`, a true here is NEW INFORMATION: the order belongs to someone else, so
 * re-read the list rather than restoring the card as if the write had merely failed.
 */
export function isPickTaken(e) {
    const code = String(e?.code ?? e?.failCode ?? "");
    const message = String(e?.message ?? "");
    return code === "PICK_TAKEN" || /already being picked by/i.test(message);
}
/** Who holds it, off the refusal — "" when the server did not say. */
export function pickHolderOf(e) {
    const named = e?.details?.currentPicker;
    if (named)
        return String(named);
    return (/already being picked by (.+?)$/i.exec(String(e?.message ?? "")) ?? [])[1] ?? "";
}
//# sourceMappingURL=errors.js.map